import { useEffect, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import {
  addToast,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
} from '@heroui/react';

import moment from 'moment';

import { FaUser } from 'react-icons/fa';

import SmallLoadingSpinner from './SmallLoadingSpinner';
import { useCheckTimesSlotAvailability } from '../../../../hook/useMutationData';
import { useFetchServiceDataById } from '../../../../hook/useQueryData';
import { useBookingReschedule } from '../../../../service/bookingService';
import CustomTextArea from './CustomTextArea';
import CommonReply from './CommonReply';
import CommonConfirmModal from './CommonConfirmModal';
import {
  ANY_STAFF_ID,
  ANY_STAFF_LABEL,
  getBookingAvailabilityMessage,
} from '../../../../utils/common';
import { useTranslation } from 'react-i18next';

type weekType = {
  day: string;
  key: number;
};

type availabilityType = {
  id: string;
  alldate: false;
  day: string;
};

const week: weekType[] = [
  { day: 'sunday', key: 0 },
  { day: 'monday', key: 1 },
  { day: 'tuesday', key: 2 },
  { day: 'wednesday', key: 3 },
  { day: 'thursday', key: 4 },
  { day: 'friday', key: 5 },
  { day: 'saturday', key: 6 },
];

const getDefaultStaffId = (staffList: any[] = []) =>
  staffList.length > 1 ? ANY_STAFF_ID : staffList[0]?.staffId || '';

type BookingResheduleProps = {
  data: any;
  onSuccessCallback?: () => void;
};

// const BookingReshedule = (data: any) => {
const BookingReshedule = ({
  data,
  onSuccessCallback,
}: BookingResheduleProps) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Date>();
  const [serviceId, setServiceId] = useState<string>('');
  const [unAvailableDates, setUnAvailableDates] = useState<number[]>([0]);
  const [timeSlots, setTimeSlots] = useState<any[]>([]);
  const [isCalendarOpen, setCalendarOpen] = useState(false);
  const [selectTimeSlot, setSelectTimeSlot] = useState<{
    timeSlotId: string;
    from: string;
    to: string;
  }>({
    timeSlotId: '',
    from: '',
    to: '',
  });

  const [selectedStaffId, setSelectedStaffId] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [noteError, setNoteError] = useState<string>('');
  const [availableStaff, setAvailableStaff] = useState<any | null>(null);
  const [availabilityMessage, setAvailabilityMessage] = useState<string | null>(
    null
  );
  const shouldShowAnyStaff = (availableStaff?.availableStaff?.length || 0) > 1;

  const endMonth = new Date();
  endMonth.setMonth(endMonth.getMonth() + 1);
  const today = new Date();

  const {
    mutateAsync,
    isPending,
    isError: isCheckTimeslotError,
  } = useCheckTimesSlotAvailability();
  const { data: service } = useFetchServiceDataById(serviceId);

  const { mutateAsync: rescheduleMutateAsync, isPending: isReschedulePending } =
    useBookingReschedule();

  const bookedDays = new Date(data?.appointmentDate);

  useEffect(() => {
    if (!data) return;
    setServiceId(data?.serviceId);
    setSelectedStaffId('');
    setSelectTimeSlot({
      timeSlotId: '',
      from: '',
      to: '',
    });
  }, [data]);

  //   HANDLE UN AVAILABLE DATES
  useEffect(() => {
    if (!service?.serviceInfo?.availability) return;

    const availability = service?.serviceInfo?.availability;

    const availableKeys = availability.map((a: availabilityType) => {
      return week.find((w: weekType) => w.day === a.day)?.key;
    });

    const unAvailableKeys = week
      .map((w) => w.key)
      .filter((key) => !availableKeys.includes(key));

    console.log('getUnMatchWeekNumber ', unAvailableKeys);
    setUnAvailableDates(unAvailableKeys);
    // Default to the booked date, not the next available weekday from today.
    // If the booked date falls on a provider-unavailable weekday, walk forward
    // to the next available date as a fallback.
    const bookedWeekday = bookedDays.getDay();
    const isBookedDayAvailable = !unAvailableKeys.includes(bookedWeekday);
    const defaultDate = isBookedDayAvailable
      ? new Date(bookedDays)
      : getNextAvailableDate(bookedDays, unAvailableKeys);
    setSelected(defaultDate);
  }, [service]);

  // find the next available date
  const getNextAvailableDate = (
    startDate: Date,
    blockedWeekdays: number[]
  ): Date => {
    const date = new Date(startDate);
    while (true) {
      if (!blockedWeekdays.includes(date.getDay())) {
        return date;
      }
      date.setDate(date.getDate() + 1);
    }
  };

  // CHECK AVAILABILITY TIMESLOT
  useEffect(() => {
    const checkBookingAvailableTimeSlot = async () => {
      if (!serviceId) return;
      if (!selected && !bookedDays) return;

      setSelectTimeSlot({
        timeSlotId: '',
        from: '',
        to: '',
      });
      setAvailableStaff(null);
      setSelectedStaffId('');
      setAvailabilityMessage(null);

      const payload: any = {
        serviceId,
        date: moment(selected || bookedDays).format('YYYY-MM-DD'),
      };

      try {
        const response = await mutateAsync(payload);
        const availabilityError = getBookingAvailabilityMessage(response);
        setAvailabilityMessage(availabilityError);

        if (response?.success) {
          const timeSlot = response?.timeSlots?.[0];
          setTimeSlots(response?.timeSlots || []);
          setSelectedStaffId(getDefaultStaffId(timeSlot?.availableStaff));
          setSelectTimeSlot(
            timeSlot
              ? {
                  from: timeSlot?.from,
                  timeSlotId: timeSlot?.timeSlotId,
                  to: timeSlot?.to,
                }
              : {
                  timeSlotId: '',
                  from: '',
                  to: '',
                }
          );
        } else {
          setTimeSlots([]);
          setSelectTimeSlot({
            timeSlotId: '',
            from: '',
            to: '',
          });
        }

        console.log('response: ', response);
        if (!response?.success) {
          addToast({
            title: t('common.error'),
            description:
              availabilityError ||
              response?.message ||
              t('become_provider.something_went_wrong'),
            radius: 'md',
            color: 'danger',
          });
        }
      } catch (error) {
        console.log('Error check time-slot availability: ', error);
      }
    };

    checkBookingAvailableTimeSlot();
  }, [selected, mutateAsync, serviceId]);

  useEffect(() => {
    const matchedSlot = timeSlots.find(
      (slot: any) => slot.timeSlotId === selectTimeSlot?.timeSlotId
    );

    setAvailableStaff(matchedSlot || null);
    setSelectedStaffId(getDefaultStaffId(matchedSlot?.availableStaff));
  }, [selectTimeSlot?.timeSlotId, timeSlots]);

  const handleReschedule = async () => {
    try {
      const payload = {
        bookingId: data?.bookingId,
        staffId: selectedStaffId,
        newDate: moment(selected).format('YYYY-MM-DD'),
        newFrom: selectTimeSlot?.from,
        newTo: selectTimeSlot?.to,
        note: note.trim(),
        timeSlotsId: selectTimeSlot?.timeSlotId,
      };

      // 1. Basic Validations
      if (!payload.bookingId)
        throw new Error(t('booking_reschedule.booking_id_missing'));
      if (!selected)
        throw new Error(t('booking_reschedule.please_select_date'));
      if (!payload.newFrom)
        throw new Error(t('booking_reschedule.please_select_time'));
      if (!payload.staffId)
        throw new Error(t('booking_reschedule.please_select_staff'));

      // 2. Note Validation (Fixed Syntax)
      if (!payload.note) {
        setNoteError(t('booking_reschedule.please_enter_reason'));
        throw new Error(t('booking_reschedule.please_enter_reason'));
      }

      if (payload.note.length < 5) {
        setNoteError(t('booking_reschedule.reason_min_length'));
        throw new Error(t('booking_reschedule.reason_min_length'));
      }

      if (payload.note.length > 200) {
        throw new Error(t('booking_reschedule.reason_max_length'));
      }

      // 3. Guard against submitting a past date (e.g. booked date from a
      //    completed booking, programmatically set as default but never
      //    changed by the user).
      const todayMidnight = new Date();
      todayMidnight.setHours(0, 0, 0, 0);
      if (selected < todayMidnight) {
        throw new Error(t('booking_reschedule.past_date_not_allowed'));
      }

      await rescheduleMutateAsync({
        data: payload,
      });

      if (onSuccessCallback) {
        onSuccessCallback();
      }

      console.log('Final payload: ', payload);
    } catch (error: any) {
      addToast({
        title: t('common.error'),
        description:
          error?.message || t('booking_reschedule.something_went_wrong'),
        color: 'danger',
      });
    }
  };

  return (
    <>
      {/* BOOKING NOTE */}
      {data?.createdAt && (
        <div>
          <p className="text-body-bold mb-2 mt-2">
            {t('booking_reschedule.last_change')}
            {moment(data?.createdAt).format('DD MMM YYYY, hh:mm A')}
          </p>
        </div>
      )}

      {data?.rescheduledNote && (
        <CommonReply
          title={t('booking_reschedule.last_change_reason')}
          description={data?.rescheduledNote || t('common.na')}
        />
      )}

      <div className="grid grid-cols-1 gap-6 mt-3">
        <div>
          <p className="text-body-bold mb-2">
            {t('booking_reschedule.select_new_date')}
          </p>
          <button
            onClick={() => setCalendarOpen(true)}
            className="w-full text-left p-2 sm:p-2 text-xs  border border-gray-300 rounded-md shadow-sm"
          >
            {selected
              ? moment(selected).format('dddd, DD MMMM YYYY')
              : t('booking_reschedule.choose_a_date')}
          </button>

          {/* -- CHANGE: DayPicker is now inside a Modal -- */}
          <Modal isOpen={isCalendarOpen} onOpenChange={setCalendarOpen}>
            <ModalContent>
              <ModalHeader>{t('booking_reschedule.select_date')}</ModalHeader>
              <ModalBody>
                <div className="flex justify-center items-center">
                  <DayPicker
                    mode="single"
                    animate
                    navLayout="around"
                    modifiersClassNames={{
                      selected: 'rounded-full bg-blue-400 text-white text-sm',
                    }}
                    // numberOfMonths={2}
                    defaultMonth={today}
                    disabled={{
                      before: new Date(),
                      dayOfWeek: unAvailableDates,
                    }}
                    startMonth={today}
                    endMonth={endMonth}
                    selected={selected}
                    onSelect={(date) => {
                      setSelected(date);
                      setCalendarOpen(false);
                    }}
                    required
                  />
                </div>
              </ModalBody>
            </ModalContent>
          </Modal>
        </div>

        {/* -- SECTION 2: Time Selection -- */}
        {selected && (
          <div>
            <p className="text-body-bold mb-2">
              {t('booking_reschedule.available_times_for', {
                date: moment(selected).format('DD MMMM'),
              })}
            </p>

            <div className="flex gap-2 pb-2 overflow-x-auto whitespace-nowrap scrollbar-visible">
              {isPending ? (
                <SmallLoadingSpinner />
              ) : isCheckTimeslotError ? (
                <p className="text-xs">
                  {t('booking_reschedule.data_not_found')}
                </p>
              ) : timeSlots.length === 0 ? (
                <div className="col-span-3">
                  <p className="text-xs">
                    {availabilityMessage ||
                      t('booking_reschedule.no_available_times')}
                  </p>
                </div>
              ) : (
                timeSlots.map((time, index) => (
                  <button
                    key={index}
                    onClick={() =>
                      setSelectTimeSlot({
                        timeSlotId: time?.timeSlotId,
                        from: time?.from,
                        to: time?.to,
                      })
                    }
                    disabled={isReschedulePending}
                    className={`flex-shrink-0 flex justify-center items-center border border-secondary/30 p-2 shadow-sm rounded-md w-36 ${
                      selectTimeSlot?.timeSlotId === time?.timeSlotId
                        ? 'bg-secondary/40 shadow-md'
                        : 'hover-primary-gradient'
                    }`}
                  >
                    <p className="text-body-bold">{`${time?.from} - ${time?.to}`}</p>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* -- SECTION 3: Staff Selection -- */}
        {selectTimeSlot?.timeSlotId && (
          <div>
            <p className="text-body-bold mb-2">
              {t('booking_reschedule.available_staff')}
            </p>
            <div className="flex gap-2 pb-2 overflow-x-auto">
              {isPending ? (
                <div className="col-span-3">
                  <SmallLoadingSpinner />
                </div>
              ) : isCheckTimeslotError ? (
                <div className="col-span-3">
                  <p className="text-xs">
                    {t('booking_reschedule.data_not_found')}
                  </p>
                </div>
              ) : availableStaff === null ? (
                <div className="col-span-3">
                  <p className="text-xs">
                    {availabilityMessage ||
                      t('booking_reschedule.no_staff_available')}
                  </p>
                </div>
              ) : (
                <>
                  {shouldShowAnyStaff && (
                    <button
                      onClick={() => setSelectedStaffId(ANY_STAFF_ID)}
                      disabled={isReschedulePending}
                      className={`flex justify-start items-center border border-secondary/30 p-2 shadow-sm rounded-md w-38 ${
                        selectedStaffId === ANY_STAFF_ID
                          ? 'bg-secondary/40'
                          : 'hover-primary-gradient'
                      }`}
                    >
                      <FaUser className="text-secondary" />
                      <p className="text-body-bold ml-2">{ANY_STAFF_LABEL}</p>
                    </button>
                  )}
                  {availableStaff?.availableStaff?.map(
                    (s: any, index: number) => (
                      <button
                        key={index}
                        onClick={() => setSelectedStaffId(s?.staffId || '')}
                        disabled={isReschedulePending}
                        className={`flex justify-start items-center border border-secondary/30  p-2 shadow-sm  rounded-md w-38 ${
                          selectedStaffId === s?.staffId
                            ? 'bg-secondary/40'
                            : 'hover-primary-gradient'
                        }`}
                      >
                        <FaUser className="text-secondary" />
                        <p className="text-body-bold ml-2">{s?.fullName}</p>
                      </button>
                    )
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* -- SECTION 4 & 5: Note and Button -- */}
        {/* These can remain as they are, they stack well vertically. */}
        <div>
          <CustomTextArea
            label={t('booking_reschedule.change_note')}
            placeholder={t('booking_reschedule.enter_note')}
            minRows={2}
            isRequired
            onValueChange={(val) => {
              setNote(val);
              setNoteError('');
            }}
            maxLength={200}
            minLength={5}
            variant="bordered"
            description={t('booking_reschedule.change_note_description')}
            isInvalid={!!noteError}
            errorMessage={noteError}
            classNames={{
              inputWrapper: 'text-body',
              errorMessage: 'text-xs',
              base: 'text-xs',
              description: 'text-xs',
            }}
            isDisabled={isReschedulePending}
          />
        </div>

        <div className="flex justify-end items-end py-3">
          <CommonConfirmModal
            buttonLabel={t('booking_reschedule.change_booking')}
            buttonColor="primary"
            title={t('booking_reschedule.change_booking_question')}
            description={t('booking_reschedule.change_booking_desc')}
            confirmBtnText={t('booking_reschedule.yes_accept')}
            confirmBtnColor="primary"
            iconType="question"
            requireReason={false}
            reasonRequired={false}
            isDisabled={isReschedulePending}
            isLoading={isReschedulePending}
            onConfirm={(reason) => {
              handleReschedule();
              console.log(reason);
            }}
          />
        </div>
      </div>
    </>
  );
};

export default BookingReshedule;
